import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UserPlus, LogIn } from "lucide-react";

interface RegisterPromptDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title?: string;
	description?: string;
	customMessage?: string;
}

/**
 * RegisterPromptDialog Component
 *
 * A reusable dialog that prompts unauthenticated users to register or login.
 * Can be customized with different titles, descriptions, and messages.
 *
 * @param open - Controls dialog visibility
 * @param onOpenChange - Callback when dialog open state changes
 * @param title - Custom dialog title (default: "Anmeldung erforderlich")
 * @param description - Custom dialog description (default: "Du musst angemeldet sein, um auf diese Funktion zuzugreifen.")
 * @param customMessage - Additional custom message to display
 */
export function RegisterPromptDialog({
	open,
	onOpenChange,
	title = "Anmeldung erforderlich",
	description = "Du musst angemeldet sein, um auf diese Funktion zuzugreifen.",
	customMessage,
}: RegisterPromptDialogProps) {
	const navigate = useNavigate();
	const [isNavigating, setIsNavigating] = useState(false);

	const handleRegister = () => {
		setIsNavigating(true);
		navigate("/auth/register");
	};

	const handleLogin = () => {
		setIsNavigating(true);
		navigate("/auth/login");
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					<DialogDescription>{description}</DialogDescription>
				</DialogHeader>

				{customMessage && (
					<div className="bg-card dark:bg-card p-4 rounded-lg border border-primary/30">
						<p className="text-sm text-foreground">{customMessage}</p>
					</div>
				)}

				<DialogFooter className="flex-col sm:flex-row gap-2">
					<Button
						type="button"
						variant="outline"
						onClick={handleLogin}
						disabled={isNavigating}
						className="flex-1"
					>
						<LogIn className="h-4 w-4 mr-2" />
						Login
					</Button>
					<Button
						type="button"
						onClick={handleRegister}
						disabled={isNavigating}
						className="flex-1"
					>
						<UserPlus className="h-4 w-4 mr-2" />
						Register
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
